import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, SITE } from "../content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenSub(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_0_0_#E5E2DD,0_8px_24px_-12px_rgba(28,28,28,0.18)]" : "border-b border-ridge-border"
      }`}
    >
      <div className="container-pad flex h-16 items-center justify-between md:h-20">
        <Link to="/" className="group flex items-center gap-3" data-testid="header-logo-link" aria-label="Ridgehawk Logistics — Home">
          <img src="/logo-icon.png" alt="" aria-hidden="true" className="h-9 w-9 md:h-11 md:w-11" />
          <span className="hidden text-[0.78rem] font-bold uppercase tracking-[0.18em] text-ridge-charcoal sm:inline-block">{SITE.wordmark}</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary" data-testid="primary-nav">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setOpenSub(link.label)}
                onMouseLeave={() => setOpenSub(null)}
                data-testid={`nav-dropdown-${link.label.toLowerCase()}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenSub((v) => (v === link.label ? null : link.label))}
                  className="nav-link inline-flex items-center gap-1 py-1"
                  aria-expanded={openSub === link.label}
                  aria-haspopup="menu"
                  data-testid={`nav-trigger-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  <ChevronDown className={`h-3 w-3 transition-transform ${openSub === link.label ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {openSub === link.label ? (
                  <div className="absolute left-1/2 top-full z-10 -translate-x-1/2 pt-2" role="menu">
                    <div className="min-w-[180px] rounded-md border border-ridge-border bg-white p-1.5 shadow-[0_24px_48px_-24px_rgba(28,28,28,0.4)]">
                      {link.children.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          role="menuitem"
                          data-testid={`nav-sub-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={({ isActive }) =>
                            `block rounded px-3 py-2 text-sm font-semibold uppercase tracking-[0.14em] transition-colors ${
                              isActive ? "bg-ridge-stone text-ridge-charcoal" : "text-ridge-charcoal hover:bg-ridge-stone hover:text-ridge-amber"
                            }`
                          }
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `nav-link relative py-1 ${isActive ? "text-ridge-charcoal after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:w-full after:bg-ridge-amber" : ""}`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
          <Link to="/quote" className="btn-accent" data-testid="nav-quote-cta">
            Get a Quote
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ridge-charcoal hover:bg-ridge-stone lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`lg:hidden ${open ? "block" : "hidden"}`} data-testid="mobile-menu">
        <div className="border-t border-ridge-border bg-white px-6 pb-8 pt-4">
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label} className="py-1">
                  <div className="px-3 pt-3 pb-1 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ridge-muted">{link.label}</div>
                  {link.children.map((sub) => (
                    <NavLink
                      key={sub.to}
                      to={sub.to}
                      data-testid={`mobile-nav-link-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `block rounded-md px-3 py-3 text-sm font-semibold uppercase tracking-[0.14em] transition-colors ${
                          isActive ? "bg-ridge-stone text-ridge-charcoal" : "text-ridge-charcoal hover:bg-ridge-stone hover:text-ridge-amber"
                        }`
                      }
                    >
                      {sub.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-3 text-sm font-semibold uppercase tracking-[0.14em] transition-colors ${
                      isActive ? "bg-ridge-stone text-ridge-charcoal" : "text-ridge-charcoal hover:bg-ridge-stone hover:text-ridge-amber"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>
          <Link to="/quote" className="btn-accent mt-4 w-full" data-testid="mobile-nav-quote-cta">Get a Quote</Link>
        </div>
      </div>
    </header>
  );
}
